/**
 * Species parameter tables.
 *
 * Growth uses respiration scaling: photosynthesis P ∝ B^(2/3) (sub-linear),
 * respiration R ∝ B (linear). Net production is zero at
 *   Bmax(light, water) = (growthRate * light * waterFactor / respRate)^3,
 * so BOTH a determinate adult size AND natural die-back under shade fall out of
 * one equation — an overshaded plant's Bmax drops below its current B and it
 * shrinks away. The growthRate/respRate *ratio* sets size and shade tolerance;
 * their overall *scale* sets speed (time to bloom). These are tuned against the
 * headless harness — see harness/run.ts and the `probe` mode.
 */
export const SPECIES_IDS = ["grass", "heather", "reed", "rowan"] as const;
export type SpeciesId = (typeof SPECIES_IDS)[number];
export const SPECIES_COUNT = SPECIES_IDS.length;

export interface SpeciesParams {
  readonly id: SpeciesId;
  readonly color: readonly [number, number, number];

  // --- growth / respiration ---
  readonly growthRate: number;
  readonly respRate: number;

  // --- allometry: smooth functions of biomass B ---
  readonly heightCoef: number;
  readonly heightExp: number;
  readonly radiusCoef: number;
  readonly radiusExp: number;
  /** Leaf area (→ shade cast) per unit biomass; scales LAI in the light pass. */
  readonly shadeFactor: number;

  // --- niche ---
  readonly moistureOpt: number;
  readonly moistureWidth: number;

  // --- reproduction ---
  /** Biomass at which the plant flowers / can set seed. */
  readonly maturityB: number;
  /** Fraction of positive net production diverted to reproductive reserve. */
  readonly reproAlloc: number;
  /** Reserve energy consumed per seed produced. */
  readonly seedCost: number;
  /** Dispersal kernel sigma in metres (wind species wide, large seeds tight). */
  readonly dispersal: number;
  /** Per-second seedbank viability decay. */
  readonly seedDecay: number;
  /** Base germination probability per seed per second at ideal conditions. */
  readonly germRate: number;
  /** Minimum light fraction for a seed to germinate. */
  readonly germLight: number;
  /** Biomass of a freshly germinated seedling. */
  readonly seedlingB: number;
  /** Below this biomass the plant fades out (die-back). */
  readonly minB: number;
  /** Heather requires pollination: seed-set scales with local conspecific density. */
  readonly needsPollinator: boolean;
}

/** Determinate adult biomass at full light/water: Bmax = (growthRate/respRate)^3. */
export function bmax(sp: SpeciesParams): number {
  const r = sp.growthRate / sp.respRate;
  return r * r * r;
}

export function height(sp: SpeciesParams, B: number): number {
  return sp.heightCoef * Math.pow(Math.max(B, 1e-6), sp.heightExp);
}

export function radius(sp: SpeciesParams, B: number): number {
  return sp.radiusCoef * Math.pow(Math.max(B, 1e-6), sp.radiusExp);
}

/** Gaussian match of a cell's moisture to the species optimum, in [0, 1]. */
export function waterFactor(sp: SpeciesParams, moisture: number): number {
  const d = (moisture - sp.moistureOpt) / sp.moistureWidth;
  return Math.exp(-0.5 * d * d);
}

export const SPECIES: Record<SpeciesId, SpeciesParams> = {
  grass: {
    id: "grass",
    color: [0.45, 0.72, 0.32],
    growthRate: 0.105,
    respRate: 0.105, // Bmax ≈ 1 at full light: fast, small, shade-intolerant
    heightCoef: 0.4,
    heightExp: 0.5,
    radiusCoef: 0.3,
    radiusExp: 0.5,
    shadeFactor: 0.8,
    moistureOpt: 0.5,
    moistureWidth: 0.35,
    maturityB: 0.55,
    reproAlloc: 0.7,
    seedCost: 0.015,
    dispersal: 4.5,
    seedDecay: 0.004,
    germRate: 0.22,
    germLight: 0.28,
    seedlingB: 0.09,
    minB: 0.06, // shade-intolerant: dies back under >~60% shade as canopy closes
    needsPollinator: false,
  },
  heather: {
    id: "heather",
    color: [0.78, 0.25, 0.66],
    growthRate: 0.0806,
    respRate: 0.0509, // Bmax ≈ 4: mid growth, moderate shade tolerance
    heightCoef: 0.345,
    heightExp: 0.4,
    radiusCoef: 0.175,
    radiusExp: 0.5,
    shadeFactor: 1.0,
    moistureOpt: 0.4,
    moistureWidth: 0.25,
    maturityB: 2.2,
    reproAlloc: 0.6,
    seedCost: 0.12,
    dispersal: 3.0,
    seedDecay: 0.003,
    germRate: 0.26,
    germLight: 0.18,
    seedlingB: 0.03,
    minB: 0.03,
    needsPollinator: true,
  },
  reed: {
    id: "reed",
    color: [0.72, 0.66, 0.28],
    growthRate: 0.1275,
    respRate: 0.070, // Bmax ≈ 6: wet-margin specialist
    heightCoef: 0.653,
    heightExp: 0.5,
    radiusCoef: 0.073,
    radiusExp: 0.5,
    shadeFactor: 0.8,
    moistureOpt: 0.9,
    moistureWidth: 0.16,
    maturityB: 3.5,
    reproAlloc: 0.55,
    seedCost: 0.12,
    dispersal: 2.5,
    seedDecay: 0.003,
    germRate: 0.28,
    germLight: 0.18,
    seedlingB: 0.03,
    minB: 0.03,
    needsPollinator: false,
  },
  rowan: {
    id: "rowan",
    color: [0.30, 0.5, 0.28],
    growthRate: 0.0757,
    respRate: 0.02436, // Bmax ≈ 30: slow, large, shade-tolerant climax tree
    heightCoef: 1.0,
    heightExp: 0.4,
    radiusCoef: 0.40,
    radiusExp: 0.5,
    shadeFactor: 1.6,
    moistureOpt: 0.55,
    moistureWidth: 0.4,
    maturityB: 18,
    reproAlloc: 0.5,
    seedCost: 0.8,
    dispersal: 4.0,
    seedDecay: 0.0015,
    germRate: 0.05,
    germLight: 0.05,
    seedlingB: 0.08,
    minB: 0.06,
    needsPollinator: false,
  },
};

export function speciesIndex(id: SpeciesId): number {
  return SPECIES_IDS.indexOf(id);
}

export const SPECIES_LIST: readonly SpeciesParams[] = SPECIES_IDS.map((id) => SPECIES[id]);
